import { useState } from 'react';
import { EmailAttachment } from '@/api/submission-scheduler/email-attachments';
import {
	addAttachments,
	deleteAttachments,
} from '@/api/submission-scheduler/emails';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';

import { columns } from './columns';

interface AttachmentTableProps {
	emailId: number;
	attachments: EmailAttachment[];
	onAttachmentsChanged: () => void | Promise<void>;
}

export function AttachmentTable({
	emailId,
	attachments,
	onAttachmentsChanged,
}: AttachmentTableProps) {
	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const table = useReactTable({
		data: attachments,
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		enableRowSelection: true,
		getRowId: (row) => String(row.attachmentId),
	});

	const handleUpload = async () => {
		if (pendingFiles.length === 0) return;

		setIsUploading(true);
		const success = await addAttachments(emailId, pendingFiles);

		if (success) {
			toast.success('Attachments uploaded successfully');
			setPendingFiles([]);
			await onAttachmentsChanged(); // Refetch attachments
		} else {
			toast.error('Failed to upload attachments');
		}
		setIsUploading(false);
	};

	const handleDelete = async () => {
		const selectedRows = table.getSelectedRowModel().rows;
		const idsToDelete = selectedRows.map((row) => row.original.attachmentId);

		if (idsToDelete.length === 0) return;

		setIsDeleting(true);
		const success = await deleteAttachments(emailId, idsToDelete);

		if (success) {
			toast.success(`${idsToDelete.length} attachment(s) deleted`);
			table.toggleAllPageRowsSelected(false);
			await onAttachmentsChanged(); // Refetch attachments
		} else {
			toast.error('Failed to delete attachments');
		}
		setIsDeleting(false);
	};

	return (
		<div className="space-y-4">
			<FormLabel>Attachments</FormLabel>

			{attachments.length > 0 && (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(header.column.columnDef.header, header.getContext())}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{table.getRowModel().rows.map((row) => (
								<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{attachments.length > 0 && (
				<Button
					type="button"
					variant="destructive"
					onClick={handleDelete}
					disabled={table.getSelectedRowModel().rows.length === 0 || isDeleting}
				>
					{isDeleting
						? 'Deleting...'
						: `Delete selected (${table.getSelectedRowModel().rows.length})`}
				</Button>
			)}

			<Card>
				<CardContent className="pt-6">
					<div className="space-y-3">
						<div>
							<FormLabel htmlFor="attachment-upload" className="text-sm font-medium">
								Add New Attachments
							</FormLabel>
							<p className="text-xs text-muted-foreground mt-1">
								Upload files to attach to this email
							</p>
						</div>

						<Input
							id="attachment-upload"
							type="file"
							multiple
							onChange={(e) => {
								if (e.target.files) {
									setPendingFiles(Array.from(e.target.files));
								}
							}}
							className="cursor-pointer"
						/>

						{pendingFiles.length > 0 && (
							<div className="rounded-md bg-muted p-3">
								<p className="text-sm font-medium">
									{pendingFiles.length} file(s) selected
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{pendingFiles.map((f) => f.name).join(', ')}
								</p>
							</div>
						)}

						<Button
							type="button"
							variant="outline"
							onClick={handleUpload}
							disabled={pendingFiles.length === 0 || isUploading}
							className="w-full"
						>
							{isUploading ? 'Uploading...' : 'Upload attachments'}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
